/**
 * Learn: eight techniques, and how often you've practised each.
 *
 * Its own tab rather than buried inside recipes, because a thing nobody can
 * find teaches nobody anything. Deliberately a short list — eight done properly
 * beats fifty as an index, and the moment this becomes a catalogue it stops
 * being something you'd work through.
 *
 * The counts are the seed of the skill tree: they record what actually
 * happened, which is why they're worth anything. A level number that went up
 * because you opened a screen would be a lie with a progress bar on it.
 */

import { TECHNIQUES } from "../features/techniques/techniques";
import type { PracticeLog } from "../features/techniques/practiceStorage";
import type { Technique } from "../features/techniques/types";
import { Icon } from "./Icon";

import "./LearnScreen.css";

interface LearnScreenProps {
  practice: PracticeLog;
  onOpen: (technique: Technique) => void;
}

export function LearnScreen({ practice, onOpen }: LearnScreenProps) {
  const tried = TECHNIQUES.filter((technique) => practice[technique.id]).length;
  const nailed = TECHNIQUES.filter(
    (technique) => practice[technique.id]?.best === "good"
  ).length;

  return (
    <div className="learn">
      <p className="learn__intro">
        The handful of things that separate food that works from food that
        doesn't — and that most recipes assume you already know.
      </p>

      {/* Two numbers, big, because they are the reason to come back. A
          sentence in grey text said the same thing and read as a footnote —
          which is what progress looks like when nobody is proud of it. */}
      <div className="learn__scores">
        <div className="learn__score">
          <span className="learn__score-value">{tried}</span>
          <span className="learn__score-label">tried</span>
        </div>
        <div className="learn__score" data-tone="good">
          <span className="learn__score-value">{nailed}</span>
          <span className="learn__score-label">looking right</span>
        </div>
        <div className="learn__score">
          <span className="learn__score-value">{TECHNIQUES.length}</span>
          <span className="learn__score-label">in the book</span>
        </div>
      </div>

      <ul className="learn__list">
        {TECHNIQUES.map((technique) => {
          const record = practice[technique.id];
          return (
            <li key={technique.id}>
              <button
                className="learn__item"
                type="button"
                data-done={record?.best === "good" || undefined}
                onClick={() => onOpen(technique)}
              >
                <span className="learn__text">
                  <span className="learn__name">{technique.name}</span>
                  <span className="learn__summary">{technique.summary}</span>
                  <span className="learn__tags">
                    <span className="learn__tag" data-kind="level">
                      {technique.level}
                    </span>
                    {record ? (
                      <span
                        className="learn__tag"
                        data-kind={record.best === "good" ? "good" : "tried"}
                      >
                        {record.attempts}{" "}
                        {record.attempts === 1 ? "attempt" : "attempts"}
                      </span>
                    ) : (
                      <span className="learn__tag">Not tried</span>
                    )}
                    {!technique.check && (
                      <span className="learn__tag">No photo check</span>
                    )}
                  </span>
                </span>

                {/* A tick once you've managed it, otherwise a chevron. The
                    words above say the same thing, so this is never the only
                    signal. */}
                <Icon
                  name={record?.best === "good" ? "check" : "chevron"}
                  className="learn__mark"
                  data-on={record?.best === "good" || undefined}
                />
              </button>
            </li>
          );
        })}
      </ul>

      <p className="learn__footnote">
        Photo feedback is one picture judged by a model. It's a second opinion,
        not a chef — six of these can be checked that way and two genuinely
        can't, which is why those two say so.
      </p>
    </div>
  );
}
